<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { bind, get } from 'lodash';
import resources from '@enums/resources';
import featureFlags from '@enums/feature-flags';
import vuexForm from '@enums/vuex-form';
import customComponents from '@enums/custom-components';
import valueTypes from '@enums/value-types';
import commonFormDialogComponent from '@/js/components/common-form-dialog.vue';
import i18n from '@/js/vue-i18n';
import validators from '@/js/validators';
import { getDefaultValuePropName, getDefaultValuePropValue } from '@/js/utils/component-utils';

const { scenarios } = resources;
const { object } = valueTypes;
const { canEditScenario, canPublishScenarios } = featureFlags;
const { fieldTypes, sourceTypes } = vuexForm;
const { componentTypes } = customComponents;

const createForm = function({
    isEditable,
    isPrivateDefault,
    isDetailsOpened,
    isNominationsOpened,
    onDetailsPanelStateChange,
    onNominationsPanelStateChange,
    scenarioDefaults,
    categoriesTree,
    updateCategoriesInStagingArea,
}) {
    return {
        vuexModule: 'scenarios',
        editable: isEditable,
        addEvent: 'createScenario',
        editEvent: 'updateScenario',
        fields: [
            {
                fieldName: 'name',
                editable: isEditable,
                type: fieldTypes.text,
                label: 'planning.createScenario.title',
                placeholder: 'planning.createScenario.titlePlaceholder',
                cssClass: 'vuex-form__name',
                isRequired: true,
                validations: [
                    {
                        message: i18n.t('validation.scenario.nameRequired'),

                        validator: validators.required,
                    },
                ],
            },
            {
                fieldName: 'briefing',
                editable: isEditable,
                type: fieldTypes.textarea,
                label: 'planning.createScenario.briefing',
                placeholder: 'planning.createScenario.briefingPlaceholder',
                cssClass: 'vuex-form__briefing',
            },
            {
                type: fieldTypes.dateRange,
                editable: isEditable,
                label: 'planning.createScenario.dates',
                cssClass: 'vuex-form__dates',
                isRequired: true,
                minDate: {
                    source: sourceTypes.context,
                    path: 'startDate',
                },
                maxDate: {
                    source: sourceTypes.context,
                    path: 'endDate',
                },
                from: {
                    fieldName: 'startDate',
                    dynamicDefaultValue: {
                        source: sourceTypes.context,
                        path: 'startDate',
                    },
                },
                to: {
                    fieldName: 'endDate',
                    dynamicDefaultValue: {
                        source: sourceTypes.context,
                        path: 'endDate',
                    },
                },
                entity: 'scenario',
                ignoreParentReadOnly: true,
                validations: [
                    {
                        message: i18n.t('validation.scenario.datesRequired'),

                        validator: validators.required,
                    },
                    {
                        validator: validators.dateRangeRequired,
                    },
                ],
            },
            {
                type: componentTypes.expansionPanel,
                isContainer: true,
                label: 'planning.createScenario.categories',
                cssClass: 'vuex-form__details',
                expansionPanelClass: 'details__wrapper',
                isOpenState: isDetailsOpened,
                change: onDetailsPanelStateChange,
                editable: isEditable,
                children: [
                    {
                        fieldName: 'subCampaignId',
                        dynamicDefaultValue: {
                            source: sourceTypes.context,
                            path: '_id',
                        },
                    },
                    {
                        fieldName: 'categories',
                        type: componentTypes.categoriesSelect,
                        tree: categoriesTree,
                        cssClass: 'vuex-form__categories',
                        editable: isEditable,
                        isRequired: true,
                        update: updateCategoriesInStagingArea,
                        [getDefaultValuePropName(
                            scenarioDefaults.categories
                        )]: getDefaultValuePropValue(scenarioDefaults.categories, {
                            source: sourceTypes.context,
                            path: 'categories',
                        }),
                    },
                    {
                        fieldName: 'customerGroups',
                        editable: isEditable,
                        type: fieldTypes.checkboxesList,
                        cssClass: 'vuex-form__customer-restrictions',
                        isRequired: true,
                        label: 'planning.customerRestrictions',
                        options: {
                            source: sourceTypes.getter,
                            identifier: 'getCheckboxListOptions',
                            module: 'scenarios',
                            params: {
                                attributeName: 'customerGroups',
                                attributeKey: 'key',
                                resource: 'clientConfig',
                                getOptionsFunction: 'getCustomerRestrictions',
                                attributePathOnChildEntity: 'offerMechanic.customerAvailability',
                                scenarioId: {
                                    source: sourceTypes.editContext,
                                    path: '_id',
                                },
                            },
                        },
                        itemText: 'description',
                        itemValue: 'key',
                        valueType: object,
                        [getDefaultValuePropName(
                            scenarioDefaults.customerRestrictions
                        )]: getDefaultValuePropValue(scenarioDefaults.customerRestrictions, {
                            source: sourceTypes.context,
                            path: 'customerGroups',
                        }),
                        validations: [
                            {
                                validator: validators.required,
                            },
                        ],
                    },
                ],
            },
            {
                type: componentTypes.expansionPanel,
                isContainer: true,
                label: 'planning.createScenario.resources',
                cssClass: 'vuex-form__nominations',
                isOpenState: isNominationsOpened,
                change: onNominationsPanelStateChange,
                editable: isEditable,
                children: [
                    {
                        type: componentTypes.nominationTabs,
                        editable: isEditable,
                        parentContextType: scenarios,
                        storeGroups: {
                            fieldName: 'storeGroups',
                            editable: isEditable,
                            options: {
                                source: sourceTypes.getter,
                                identifier: 'getCheckboxListOptions',
                                module: 'scenarios',
                                params: {
                                    attributeName: 'storeGroups',
                                    attributeKey: 'key',
                                    resource: 'storeGroups',
                                    getOptionsFunction: 'getAvailableStoreGroupsOptions',
                                    startDate: {
                                        source: sourceTypes.editContext,
                                        path: 'startDate',
                                    },
                                    selectedStoreGroups: {
                                        source: sourceTypes.editContext,
                                        path: 'storeGroups',
                                    },
                                    attributePathOnChildEntity: 'storeGroups',
                                    getUserAccessOptionsMap: 'userStoreGroupsMap',
                                    scenarioId: {
                                        source: sourceTypes.editContext,
                                        path: '_id',
                                    },
                                },
                            },
                            itemText: 'description',
                            itemValue: 'key',
                            valueType: object,
                            [getDefaultValuePropName(
                                scenarioDefaults.storeGroups
                            )]: getDefaultValuePropValue(scenarioDefaults.storeGroups, {
                                source: sourceTypes.context,
                                path: 'storeGroups',
                            }),
                        },
                        resources: {
                            isRequired: true,
                            fieldName: 'resources',
                            editable: isEditable,
                            itemText: 'description',
                            itemValue: 'key',
                            options: {
                                source: sourceTypes.getter,
                                identifier: 'getResourceOptions',
                                module: 'scenarios',
                                params: {
                                    parentModule: 'subCampaigns',
                                    parentGetter: 'selectedSubCampaign',
                                    contextId: {
                                        source: sourceTypes.editContext,
                                        path: '_id',
                                    },
                                    childModule: 'promotions',
                                    childGetter: 'getPromotionsByScenarioId',
                                },
                            },
                            [getDefaultValuePropName(
                                scenarioDefaults.promoResources
                            )]: getDefaultValuePropValue(scenarioDefaults.promoResources, {
                                source: sourceTypes.getter,
                                identifier: 'getDefaultResources',
                                module: 'scenarios',
                                params: {
                                    resources: {
                                        source: sourceTypes.context,
                                        path: 'resources',
                                    },
                                },
                            }),
                        },
                        defaultStoreGroups: {
                            fieldName: 'defaultStoreGroups',
                            editable: isEditable,
                            options: {
                                source: sourceTypes.getter,
                                identifier: 'getSelectedScenarioStoreGroupOptions',
                                module: 'scenarios',
                                params: {
                                    scenarioId: {
                                        source: sourceTypes.editContext,
                                        path: '_id',
                                    },
                                },
                            },
                            itemText: 'description',
                            itemValue: 'key',
                            valueType: object,
                        },
                        defaultResources: {
                            isRequired: true,
                            fieldName: 'defaultResources',
                            editable: isEditable,
                            itemText: 'description',
                            itemValue: 'key',
                            options: {
                                source: sourceTypes.getter,
                                identifier: 'getSelectedScenarioResourceOptions',
                                module: 'scenarios',
                                params: {
                                    scenarioId: {
                                        source: sourceTypes.editContext,
                                        path: '_id',
                                    },
                                },
                            },
                        },
                    },
                ],
            },
            {
                fieldName: 'isPrivate',
                defaultValue: isPrivateDefault,
                editable: isEditable,
            },
            {
                fieldName: 'isFavourite',
                defaultValue: false,
            },
            {
                fieldName: 'workflowState',
                defaultValue: [],
            },
        ],
    };
};

export default {
    extends: commonFormDialogComponent,

    data: () => ({
        resource: scenarios,
        btnCancelId: 'scenarioCancelId',
        btnConfirmDialogCancel: 'scenarioConfirmDialogCancelId',
        btnConfirmId: 'scenarioBtnConfirmId',
        isDetailsOpened: null,
        isNominationsOpened: null,
        hasConfirmationDialog: false,
    }),

    computed: {
        ...mapState('clientConfig', ['toggleLogic', 'additionalDetailsDefaultSelection']),
        ...mapGetters('subCampaigns', ['selectedSubCampaign']),
        ...mapGetters('hierarchy', ['getCategoriesTree']),
        ...mapGetters('scenarios', ['getStagingAreaField', 'getPromotionCategoriesByScenarioId']),

        form() {
            return createForm({
                isEditable: !!this.toggleLogic[canEditScenario],
                isPrivateDefault: !!this.toggleLogic[canPublishScenarios],
                isDetailsOpened: this.isDetailsOpened,
                isNominationsOpened: this.isNominationsOpened,
                onDetailsPanelStateChange: bind(
                    this.onExpansionPanelStateChange,
                    this,
                    'isDetailsOpened'
                ),
                onNominationsPanelStateChange: bind(
                    this.onExpansionPanelStateChange,
                    this,
                    'isNominationsOpened'
                ),
                scenarioDefaults: this.additionalDetailsDefaultSelection.scenario,
                categoriesTree: this.getCategoriesTree({
                    parentCategories: this.selectedSubCampaign.categories,
                    childCategories: this.getPromotionCategoriesByScenarioId({
                        scenarioId: get(this.editContext, '_id'),
                    }),
                    selectedCategories: this.getStagingAreaField({
                        namespace: this.editContext ? this.editContext._id : 'default',
                        fieldName: 'categories',
                    }),
                    isEditable: !!this.toggleLogic[canEditScenario] && !this.isReadOnly,
                }),
                updateCategoriesInStagingArea: this.updateCategoriesInStagingArea,
            });
        },

        labels() {
            return {
                btnText: 'planning.buttons.createScenario',
                heading: this.editMode
                    ? 'planning.editScenario.heading'
                    : 'planning.createScenario.heading',
            };
        },
    },
    methods: {
        ...mapActions('scenarios', ['setStagingAreaField']),

        setModalExpansionPanelsState() {
            this.setDetailsPanelState();
            this.setNominationsPanelState();
        },

        setDetailsPanelState() {
            this.setExpansionPanelState('isDetailsOpened', ['customerGroups', 'categories']);
        },

        setNominationsPanelState() {
            this.setExpansionPanelState('isNominationsOpened', ['storeGroups', 'resources']);
        },

        updateCategoriesInStagingArea(selectedCategories) {
            const namespace = this.editContext ? this.editContext._id : 'default';

            this.setStagingAreaField({
                namespace,
                fieldName: 'categories',
                value: selectedCategories,
            });
        },
    },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.campaign-container {
    color: $promo-text-colour;
    max-width: 83rem;

    :deep(.vuex-form) {
        display: grid;
        grid-template:
            'title description dates'
            'link description workflow'
            'details details details'
            'store-groups store-groups store-groups'
            'nominations nominations nominations '
            'tags tags tags'
            'children children children'
            'rate-cards rate-cards rate-cards'
            / 1fr 1fr 1fr;
    }

    :deep(.vuex-form__dates) {
        grid-area: dates;
    }

    :deep(.vuex-form__link) {
        grid-area: link;
    }

    :deep(.vuex-form__workflow) {
        grid-area: workflow;
    }

    :deep(.vuex-form__details) {
        grid-area: details;
        border-top: $component-border-divider;
        margin: 0 !important;

        .details {
            &__wrapper {
                display: grid;
                grid-template:
                    'categories . . regions'
                    'promo-resources custom-restrictions . .';
            }
        }
    }

    :deep(.vuex-form__nominations) {
        grid-area: nominations;
        border-top: $component-border-divider;
        margin: 0 !important;
        padding-bottom: 1rem;
    }

    :deep(.vuex-form__tags) {
        grid-area: tags;
        border-top: $component-border-divider;
        margin: 0 !important;
        padding-bottom: 1rem;
    }

    :deep(.vuex-form__children) {
        grid-area: children;
        border-top: $component-border-divider;
        margin: 0 !important;
        padding-bottom: 1rem;
    }

    :deep(.vuex-form__rate-cards) {
        grid-area: rate-cards;
        border-top: $component-border-divider;
        margin: 0 !important;
        padding-bottom: 1rem;
    }

    :deep(.vuex-form__name) {
        grid-area: title;
        padding-right: 2rem;
        width: 26.5rem;
    }

    :deep(.vuex-form__briefing) {
        grid-area: description;
        padding-right: 2rem;
        width: 38.5rem;
    }

    .rtls-expansion-panel-content {
        .v-expansion-panel-content__wrap {
            display: contents;
        }
    }

    :deep(.vuex-form__store-groups) {
        grid-area: store-groups;
        border-top: $component-border-divider;
        margin: 0 !important;
        padding-bottom: 1rem;
    }

    :deep(.vuex-form__store-groups-selector) {
        display: flex;
        min-width: 50%;
        max-width: 100%;
    }

    :deep(.vuex-form__promo-resources) {
        grid-area: promo-resources;
        padding-bottom: 1.4rem;

        .icon-checkboxes-list-container__control {
            padding: 0;
            display: flex;
            position: relative;
            border-right: $component-border-divider;

            .v-input--checkbox.error--text {
                padding-bottom: 1rem;
            }

            .v-messages.error--text {
                position: absolute;
                top: 2.5rem;
                left: 0;
            }
        }
    }

    :deep(.vuex-form__categories) {
        grid-area: categories;
        grid-column: 1 / span 2;
    }

    :deep(.vuex-form__customer-restrictions) {
        grid-area: custom-restrictions;
        padding: 0;

        .checkboxes-list-container__control {
            display: flex;
            position: relative;

            .v-messages.error--text {
                position: absolute;
                top: 2rem;
                left: 0;
            }

            .rtls-checkbox {
                margin-right: 1.5rem;
            }
        }
    }

    &__bottom-container {
        display: flex;
        align-items: center;

        .author-container {
            display: flex;
            justify-content: space-between;
            line-height: 2.1rem;
            font-size: 1.2rem;
            font-weight: 600;

            & > p {
                margin-right: 1.3rem;
            }
        }
        .v-divider {
            margin-right: 1.3rem;
            margin-left: 1.3rem;
        }

        &-right {
            display: flex;
            margin-left: auto;
        }
    }

    &__author-avatar {
        background-color: $promo-text-colour;
        font-size: 1rem;
        font-weight: 400;

        .white--text {
            color: $promo-white;
        }
    }

    &__last-updated {
        font-size: 1.2rem;
        font-weight: 600;
        .date {
            font-weight: 400;
        }
    }
}

.subcampaign-container {
    max-width: 90rem;

    :deep(.vuex-form) {
        .rtls-expansion-panel-content {
            padding-top: 1.5rem;
        }
    }
}
</style>
